/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { useCurrentTripContext } from "../../../../context/currentTripContext/currentTripContext";
import { iCurrentTripContext } from "../../../../context/currentTripContext/currentTripContextInterface";
import { useNavigationUrl } from "../../../../hooks/useNavigationUrl";
import { ROUTES } from "../../../../constant";
import { ONBOARDING_OPEN_FROM, ScreenTypes } from "../../../../enums";
import { DialogSelected } from "../../../../constant/common";
import { consoleLog } from "../../../../utils/consoleLog/consoleLog";
import { rebookLog } from "../../../../utils/debug/rebookDebugLog";
import { cleanQueryValue } from "../../../../helpers/cleanQueryValue";
import { getTemplateDetails } from "../../../../api/authentication/authentication";
import { isValidJWT } from "../../../../utils/authentication/isValidJWT";
import { usePixelEvent } from "../../../../hooks/usePixelEvent";
import { GA_EVENTS, GA_LABEL_ENUMS } from "../../../../constant/google-analytics/GAEvents";
import { savePostLoginRedirect } from "../../../../hooks/usePostLoginRedirect";
import { isEmbedMode } from "../../../../utils/embedMode";
import { MOBILE_MEDIA_QUERY } from "../../../../constant/breakpoints";
import { MCP_CONTINUE_PATH } from "../../../../constant/mcpWidget";

export const useAppNavigationLogic = (
  updatePromoCode: (code?: string) => void,
  getSingleHistoryDetail: (data: any) => Promise<void>
) => {
  const {
    userInfo,
    utmParams,
    setOnBoardingStep,
    setOnboardingOpenFrom,
    setFirstScreen,
    setScreen,
    accessJWT,
    setTemplateDetails,
    setLoginCreateAccountState,
    screen,
    firstScreen,
    isUserNavigatingFromPaymentGateway,
    triggeronBoardingEvent,
    setTriggeronBoardingEvent,
    pixelEventState,
  }: iGlobalContext = useContext(StateContext);

  const { currentTripData }: iCurrentTripContext = useCurrentTripContext();
  const { sendPixelEvent } = usePixelEvent();

  const navigate = useNavigate();
  const location = useLocation();
  const { constructNavigationUrl, navigateWithParams } = useNavigationUrl();
  const { pathname } = location;
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (
      triggeronBoardingEvent &&
      location.pathname === "/" &&
      (pixelEventState.signUpPixelEventSent || pixelEventState.loginPixelEventSent)
    ) {
      sendPixelEvent("startChat", {
        event: GA_EVENTS[GA_LABEL_ENUMS.onboarding_itinerary_creation_auto]?.label,
      });
      setTriggeronBoardingEvent(false);
    }
  }, [triggeronBoardingEvent, location.pathname, pixelEventState.signUpPixelEventSent]);

  const handlePreferenceNav = useCallback(() => {
    const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    if (isMobile) {
      navigateWithParams(ROUTES.preferences);
    } else {
      setFirstScreen(ScreenTypes.newPreferences);
    }
  }, [navigateWithParams, setFirstScreen]);

  // Parse URL params on mount
  useEffect(() => {
    try {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const search = window.location.search.replace("?", "");
      const isUpdatePreference = search.startsWith("update-preference");
      const data = urlParams.get("itineraryId");
      const pc = searchParams.get("pc");

      const promoCode = cleanQueryValue(pc) || cleanQueryValue(urlParams.get("pc"));
      updatePromoCode(promoCode);
      if (isUpdatePreference) {
        handlePreferenceNav();
      }
      if (data && userInfo?.loggedIn && !pathname.startsWith(ROUTES.PaymentSuccess)) {
        setOnboardingOpenFrom({
          from: ONBOARDING_OPEN_FROM.websiteCard,
          data: decodeURIComponent(data)?.replace(/\+/g, " ").replace(/=/g, "").replace(/\?/g, ""),
        });
        getSingleHistoryDetail(data);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Logic intended to run once on mount

  // Redirects based on auth status
  useEffect(() => {
    //
    if (isEmbedMode() && pathname.startsWith("/portal")) return;
    if (
      !userInfo?.loggedIn &&
      !pathname.startsWith("/preview") &&
      !pathname.startsWith("/onboarding") &&
      !pathname.startsWith("/resetpassword") &&
      !pathname.startsWith("/app") &&
      !pathname.startsWith(ROUTES.Tos) &&
      !pathname.startsWith(ROUTES.PrivacyPolicy) &&
      !pathname.startsWith(MCP_CONTINUE_PATH)
    ) {
      if (pathname.startsWith(ROUTES.ItineraryDetails)) {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const data = urlParams.get("itineraryId");
        if (data && !pathname.startsWith(ROUTES.PaymentSuccess)) {
          setOnBoardingStep("singUp");
          return;
        }
      }
      // Persist deep-link routes (currently /shared/<id>) so post-login resume
      // can restore the original URL after the onboarding flow lands the user
      // on Home. usePostLoginRedirect (called from App.tsx) reads this back.
      savePostLoginRedirect(pathname, window.location.search);
      // Pre-login share-link flow: /shared/<itineraryId>. Extract the id
      // from the pathname and route the user through the sign-up screen
      // exactly like /itinerary?itineraryId=…. The actual context-data
      // capture + redirect happen in the second useEffect below; here we
      // only flip onBoardingStep so the right screen renders post-redirect.
      if (pathname.startsWith("/shared/")) {
        const sharedId = pathname.split("/")[2];
        if (sharedId) {
          setOnBoardingStep("singUp");
          return;
        }
      }
      rebookLog("AUTH-REDIRECT → /onboarding (effect 1: !loggedIn, route not allow-listed)", {
        pathname,
        loggedIn: userInfo?.loggedIn ?? null,
      });
      const navigationUrl = constructNavigationUrl(ROUTES.onBoarding);
      navigate(navigationUrl);
    }
  }, [pathname, userInfo?.loggedIn, utmParams]);

  useEffect(() => {
    if (isEmbedMode() && pathname.startsWith("/portal")) return;
    if (
      !userInfo?.loggedIn &&
      !pathname.startsWith("/preview") &&
      !pathname.startsWith("/resetpassword") &&
      !pathname.startsWith("/app") &&
      !pathname.startsWith(ROUTES.Tos) &&
      !pathname.startsWith(ROUTES.PrivacyPolicy) &&
      !pathname.startsWith(MCP_CONTINUE_PATH)
    ) {
      if (pathname.startsWith(ROUTES.ItineraryDetails)) {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const data = urlParams.get("itineraryId");
        if (data && !pathname.startsWith(ROUTES.PaymentSuccess)) {
          setOnboardingOpenFrom({
            from: ONBOARDING_OPEN_FROM.websiteCard,
            data: decodeURIComponent(data)?.replace(/\+/g, " ").replace(/=/g, "").replace(/\?/g, ""),
          });
        }
      }
      // Pre-login share-link data capture: /shared/<itineraryId>. Save the
      // id into onboardingOpenFrom so the sign-up screen's preview hook
      // (usePreviewItineraryFromUrl) picks it up and fetches the city for
      // the "Your trip to {city} is almost there!" headline.
      //
      // `from` MUST be `sharedLink`, NOT `websiteCard`. The websiteCard
      // post-login handlers call `getSingleHistoryDetail(data, ...)` which
      // expects a chat-history id (the /itinerary?itineraryId=... flow). A
      // /shared/<id> id is a TravelPlan/Itinerary id; the history lookup
      // returns empty and the handler resets firstScreen/screen to "", so
      // even after `usePostLoginRedirect` puts the user back on /shared/<id>
      // and ViewShareItinerary sets firstScreen=topDestination, the late-
      // resolving history fetch wipes it and the user lands on a blank Home.
      // sharedLink falls through to the default case (just navigate Home
      // with empty screens), letting usePostLoginRedirect drive the flow.
      if (pathname.startsWith("/shared/")) {
        const sharedId = pathname.split("/")[2];
        if (sharedId) {
          setOnboardingOpenFrom({
            from: ONBOARDING_OPEN_FROM.sharedLink,
            data: decodeURIComponent(sharedId)?.replace(/\+/g, " ").replace(/=/g, "").replace(/\?/g, ""),
          });
        }
      }
      if (pathname.startsWith("/onboarding")) return;
      savePostLoginRedirect(pathname, window.location.search);
      rebookLog("AUTH-REDIRECT → /onboarding (effect 2: !loggedIn, route not allow-listed)", {
        pathname,
        loggedIn: userInfo?.loggedIn ?? null,
      });
      const navigationUrl = constructNavigationUrl(ROUTES.onBoarding);
      navigate(navigationUrl);
    }
  }, [utmParams]);

  // BookingInProgress logic
  useEffect(() => {
    if (
      !userInfo?.loggedIn &&
      pathname.startsWith(ROUTES.BookingInProgress) &&
      !pathname.startsWith("/resetpassword") &&
      !pathname.startsWith("/app") &&
      !pathname.startsWith(ROUTES.Tos) &&
      !pathname.startsWith(ROUTES.PrivacyPolicy)
    ) {
      consoleLog(["PATH NAME", pathname, ScreenTypes.newBooking]);
      setOnBoardingStep("singUp");

      const navigationUrl = constructNavigationUrl(ROUTES.onBoarding);
      navigate(navigationUrl);

      setTimeout(() => {
        setFirstScreen("");
        setScreen(ScreenTypes.newBooking);
        setOnboardingOpenFrom({ from: ONBOARDING_OPEN_FROM.bookingWithouFilter, data: "" });
      }, 1000);
    }
  }, [utmParams]);

  // Template details
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const template = params.get("tid");

    const paramValue = template?.replace(/"/g, "") || "";
    const fetchData = async () => {
      if (!paramValue) return;
      if (isValidJWT(accessJWT)) {
        try {
          const response: any = await getTemplateDetails(accessJWT, {
            paramValue,
          });

          setTemplateDetails(response.data);
        } catch (error) {
          console.error("Error", error);
        }
      }
    };

    fetchData();
  }, [accessJWT]);

  // Update User Logic
  useEffect(() => {
    const isUpdateUser = location.pathname.includes("update-user") || location.search.includes("update-user");
    if (!isUpdateUser) return;
    setLoginCreateAccountState({
      flag: true,
      page: userInfo.loggedIn ? DialogSelected.EDIT_ACCOUNT : DialogSelected.LOGIN,
    });
  }, [location.pathname, location.search, userInfo.loggedIn, setLoginCreateAccountState]);

  // Auto-open login modal when URL path is /login. Unauth users: auth-redirect effect above moves
  // pathname → /onboarding (params preserved via constructNavigationUrl) and
  // the modal renders over the signup page; closing the modal leaves them on
  // /onboarding with all original params intact. Logged-in users: silently
  // sent Home (no modal needed; avoids the catch-all NotFound page).
  useEffect(() => {
    const isLoginPath = /^\/login\/?$/.test(location.pathname);
    if (!isLoginPath) return;
    if (userInfo?.loggedIn) {
      navigate(`${ROUTES.Home}${location.search}`, { replace: true });
      return;
    }
    setLoginCreateAccountState({ flag: true, page: DialogSelected.LOGIN });
  }, [location.pathname, location.search, userInfo?.loggedIn, navigate, setLoginCreateAccountState]);

  // Navigation cleanup — skip when showing trip details (e.g. after "Go to trip")
  useEffect(() => {
    if (!isUserNavigatingFromPaymentGateway && screen !== ScreenTypes.TripDetails)
      if (
        (!currentTripData || (JSON.stringify(currentTripData) === "{}" && firstScreen === "")) &&
        screen !== ScreenTypes.newBooking &&
        firstScreen !== ""
      ) {
        setScreen("");
        setFirstScreen("");
      }
  }, [currentTripData, firstScreen, isUserNavigatingFromPaymentGateway, screen, setFirstScreen, setScreen]);
};
